import { useState } from "react";
import { api, ApiError, type User } from "../api";
import { btnPrimary } from "../icons";
import {
  Caption,
  DESKTOP_CARD,
  Field,
  Subtitle,
  Title,
  mobileWrap,
} from "./auth-ui";

interface Props {
  isDesktop: boolean;
  onSignedIn: (user: User) => void;
  onSwitchToSignup: () => void;
}

export function LoginScreen({ isDesktop, onSignedIn, onSwitchToSignup }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = email.trim().length > 0 && password.length >= 8 && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      const user = await api.login({ email: email.trim(), password });
      onSignedIn(user);
    } catch (e) {
      if (e instanceof ApiError && e.code === "INVALID_CREDENTIALS") {
        setError("Invalid email or password");
      } else {
        setError(e instanceof Error ? e.message : "Sign in failed");
      }
      setSubmitting(false);
    }
  };

  return (
    <div style={isDesktop ? DESKTOP_CARD : mobileWrap("60px 24px 30px")}>
      <Caption>Sign in</Caption>
      <Title>Welcome back</Title>
      <Subtitle>Enter your email and password to continue.</Subtitle>

      <Field
        label="Email"
        type="email"
        inputMode="email"
        value={email}
        onChange={(v) => {
          setEmail(v);
          setError(null);
        }}
        placeholder="you@example.com"
        autoFocus
      />

      <Field
        label="Password"
        type="password"
        value={password}
        onChange={(v) => {
          setPassword(v);
          setError(null);
        }}
        placeholder="••••••••"
        onSubmit={submit}
      />

      {error && (
        <div style={{ marginTop: -6, marginBottom: 10, fontSize: 13, color: "var(--danger)" }}>
          {error}
        </div>
      )}

      <div style={{ flex: 1 }} />

      <button disabled={!canSubmit} onClick={submit} style={btnPrimary(canSubmit)}>
        {submitting ? "Signing in…" : "Sign in"}
      </button>

      <button
        onClick={onSwitchToSignup}
        style={{
          marginTop: 14,
          background: "transparent",
          border: "none",
          color: "var(--ink-2)",
          fontSize: 14,
          fontWeight: 500,
          textAlign: "center",
        }}
      >
        New here? <span style={{ color: "var(--ink)", fontWeight: 600 }}>Create an account</span>
      </button>
    </div>
  );
}
